import { useEffect, useState } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { useLang } from "@/lib/lang-context";
import { SeoHead, sheetJsonLd } from "@/lib/seo";
import { getAnalysis } from "@/lib/api";
import EvaluationSheet from "@/components/EvaluationSheet";
import { ArrowLeft, ArrowRight, MapPin } from "lucide-react";
import { Breadcrumb, breadcrumbJsonLd } from "@/components/Breadcrumb";
import { CITY_SLUGS, CITIES } from "@/lib/seo-cities";

/**
 * Map a free-form town name back to a supported city landing slug, so
 * sheets for Lecce / Ostuni / etc. cross-link to their dedicated SEO page.
 * Defensive: case-insensitive, ignores punctuation.
 */
const slugForTown = (town) => {
  if (!town) return null;
  const norm = town.toLowerCase().trim();
  return CITY_SLUGS.find((s) => CITIES[s].name.toLowerCase() === norm) || null;
};

/**
 * Public, no-auth route for sharing an Evaluation Sheet via a permalink.
 *
 * The accompanying backend route `/api/share/:id` serves Open Graph tags for
 * social crawlers; this React route is what humans actually land on once they
 * click the link in their feed / WhatsApp / email.
 */
export default function SharedSheet() {
  const { id } = useParams();
  const location = useLocation();
  const { t } = useLang();
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const a = await getAnalysis(id);
        if (!cancelled) setAnalysis(a);
      } catch (e) {
        if (!cancelled) setError(e?.response?.status || "error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const citySlug = analysis ? slugForTown(analysis.town) : null;
  const breadcrumbItems = analysis
    ? [
        { label: "Home", to: "/" },
        ...(citySlug
          ? [{ label: CITIES[citySlug].name, to: `/citta/${citySlug}` }]
          : []),
        { label: `Report · ${analysis.total_score ?? "?"}/10` },
      ]
    : null;

  return (
    <main data-testid="shared-sheet-page" className="max-w-5xl mx-auto px-6 md:px-10 py-12">
      {analysis && (
        <SeoHead
          title={`${analysis.total_score ?? "?"}/10 · ${analysis.status || ""} · ${analysis.town || "Salento"} — Deal Rating`}
          description={
            analysis.economic_analysis ||
            analysis.technical_note ||
            `Valutazione immobile a ${analysis.town || "Salento"}: punteggio ${analysis.total_score}/10 vs dati OMI.`
          }
          path={location.pathname}
          type="article"
          jsonLd={[
            sheetJsonLd(analysis, location.pathname),
            breadcrumbJsonLd(breadcrumbItems),
          ].filter(Boolean)}
        />
      )}
      {breadcrumbItems && <div className="mb-4"><Breadcrumb items={breadcrumbItems} /></div>}
      <Link
        to="/"
        data-testid="shared-sheet-back"
        className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-[#6C706D] hover:text-[#282C2A] transition-colors mb-8"
      >
        <ArrowLeft size={13} strokeWidth={1.5} />
        {t("share.back")}
      </Link>

      {error === 404 ? (
        <div data-testid="shared-sheet-not-found" className="border-2 border-dashed border-[#E6E4DD] py-16 px-6 text-center">
          <p className="text-base text-[#6C706D]">{t("share.notFound")}</p>
        </div>
      ) : analysis ? (
        <>
          <EvaluationSheet a={analysis} />
          {citySlug && (
            <Link
              to={`/citta/${citySlug}`}
              data-testid="sheet-city-link"
              className="mt-10 block border-2 border-[#282C2A] bg-[#F0EFEB] p-6 hover:bg-[#E6E4DD] transition-colors group"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="label-eyebrow mb-2 flex items-center gap-2">
                    <MapPin size={12} strokeWidth={1.75} /> Esplora la zona
                  </div>
                  <h3 className="font-serif-display text-2xl md:text-3xl text-[#282C2A] leading-tight">
                    Comprare casa a {CITIES[citySlug].name}
                  </h3>
                  <p className="mt-2 text-sm text-[#6C706D] leading-relaxed">
                    Quotazioni OMI, vincoli, sub-mercati e altri esempi di annunci analizzati in zona.
                  </p>
                </div>
                <ArrowRight
                  size={24}
                  strokeWidth={1.5}
                  className="text-[#4A5D23] shrink-0 mt-2 group-hover:translate-x-1 transition-transform"
                />
              </div>
            </Link>
          )}
        </>
      ) : error ? (
        <div className="text-sm font-mono text-[#A0522D]">{t("share.error")}</div>
      ) : (
        <div className="text-sm font-mono text-[#6C706D]">{t("share.loading")}</div>
      )}
    </main>
  );
}
